import React, { useEffect, useState } from 'react';
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { LoadableAfterPaint } from '@confluence/loadable';
import { withErrorBoundary, Attribution } from '@confluence/error-boundary';
import { BannerStateContainer } from '@confluence/banners';
import { fg } from '@confluence/feature-gating';
import { useIsProductAdmin } from '@confluence/current-user';
import { confluenceLocalStorageInstance as localStorage, keys } from '@confluence/storage-manager';

export const BANNER_NAME = './Nav4PromptGlobalBanner';
import { useIsNav4Enabled } from '@confluence/nav4-enabled';

import { BANNER_HEIGHT, Nav4PromptBannerComponent } from './Nav4PromptBannerComponent';

/**
 * Conditions to show Nav4PromptBanner
 * - Nav3 users
 * - Site admins
 * - Do not show if banner has been previously dismissed
 */

const Nav4PromptBannerContainerComponent = ({
	bannerState,
}: {
	bannerState: BannerStateContainer;
}) => {
	const { isProductAdmin } = useIsProductAdmin();
	const [isDismissed, setDismissed] = useState<boolean>(
		localStorage.getItem(keys.NAV4_PROMPT_BANNER_FIRST_DISMISSED),
	);
	const isNav4Enabled = useIsNav4Enabled();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const showBanner =
		!isDismissed && !isNav4Enabled && isProductAdmin && fg('confluence_nav4_admin_reminder_banner');

	const handleOnDismiss = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'banner',
				actionSubjectId: 'nav4PromptBanner',
				source: 'nav4PromptBanner',
			},
		}).fire();

		// Because there is about a minute latency for TAP to update the trait after the banner is dismissed,
		// we set some data in localStorage to cover the gap.
		localStorage.setItem(keys.NAV4_PROMPT_BANNER_FIRST_DISMISSED, 'true');

		bannerState.hide(BANNER_NAME);
		setDismissed(true);
	};

	useEffect(() => {
		if (showBanner) {
			bannerState.show(BANNER_NAME, BANNER_HEIGHT);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return showBanner ? <Nav4PromptBannerComponent onDismiss={handleOnDismiss} /> : null;
};

export const Nav4PromptBanner = withErrorBoundary({
	attribution: Attribution.DISCO,
})(() => (
	<Subscribe to={[BannerStateContainer]}>
		{(bannerState: BannerStateContainer) => (
			<Nav4PromptBannerContainerComponent bannerState={bannerState} />
		)}
	</Subscribe>
));

export const Nav4PromptBannerLoadable = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-Nav4PromptBanner" */ './Nav4PromptBanner'))
			.Nav4PromptBanner,
	name: 'Nav4PromptBanner',
});
